export type DateTimeValue = Date | number | string;

const INTL_LOCALES: Record<string, string> = {
  cs: 'cs-CZ',
  en: 'en-GB',
  sk: 'sk-SK',
};

/**
 * Resolves an application language or regional variant to its Intl locale.
 */
function resolveIntlLocale(language: string) {
  const normalized = language.trim().toLowerCase();
  const supportedLanguage = Object.keys(INTL_LOCALES).find(
    (candidate) => normalized === candidate || normalized.startsWith(`${candidate}-`)
  );
  return INTL_LOCALES[supportedLanguage ?? 'sk'];
}

/**
 * Parses API datetimes and date-only values without shifting date-only values
 * across time zones.
 */
function parseDateTime(value: DateTimeValue) {
  if (value instanceof Date) {
    return new Date(value.getTime());
  }

  if (typeof value === 'string') {
    const dateOnly = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnly) {
      return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
    }
  }

  return new Date(value);
}

/**
 * Formats a date using the selected application locale.
 */
export function formatLocaleDate(
  value: DateTimeValue,
  language: string,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'long' }
) {
  const date = parseDateTime(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(resolveIntlLocale(language), options).format(date);
}

/**
 * Formats a date and time using the selected application locale.
 */
export function formatLocaleDateTime(value: DateTimeValue, language: string) {
  const date = parseDateTime(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(resolveIntlLocale(language), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

/**
 * Formats a time using the selected application locale.
 */
export function formatLocaleTime(value: DateTimeValue, language: string) {
  const date = parseDateTime(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(resolveIntlLocale(language), { timeStyle: 'short' }).format(date);
}
